import { entitiesAround, entityName, type StoryCtx, type StoryNode } from '@dispositio/core';

/** Das angefangene `@…` direkt vor dem Cursor. `start` ist die Position des „@“. */
export interface MentionToken {
  start: number;
  query: string;
}

const TOKEN = /(?:^|[^@])@([A-Za-z0-9_]*)$/;

/**
 * Liefert das `@`-Stück vor dem Cursor, sonst null. Das „@“ darf direkt am Wort hängen („Martin@“ wie „Martin @“); eine fertige
 * E-Mail-Adresse („a@b.de“) zählt nicht mehr, weil hinter dem „@“ dann Zeichen außerhalb eines Schlüssels stehen.
 */
export function mentionTokenAt(text: string, caret: number): MentionToken | null {
  const m = TOKEN.exec(text.slice(0, caret));
  if (!m) return null;
  const query = m[1]!;
  return { start: caret - query.length - 1, query };
}

/** Ein Eintrag, den man per `@schlüssel` im Text verwenden kann. */
export interface RefOption {
  entityId: string;
  /** Schlüssel, der eingefügt wird (bei Treffer auf eine weitere Bezeichnung diese) */
  refKey: string;
  /** Alle Schlüssel des Eintrags: Hauptschlüssel und weitere Bezeichnungen */
  keys: string[];
  title: string;
  typeId: string;
  typeName: string;
  typeColor: string;
  /** Kommt in der Szene des Textes vor (verbunden oder im selben Node) bzw. ist ein Foundational */
  near: boolean;
}

/** Alle Einträge des Projekts als Auswahl; Einträge der Szene zuerst, dann nach Klasse und Titel. */
export function refOptions(ctx: StoryCtx, node?: StoryNode): RefOption[] {
  const near = new Set(node ? entitiesAround(ctx, node).map((e) => e.id) : []);
  return ctx.story.entities
    .map((e): RefOption & { sort: number } => {
      const type = ctx.types.get(e.typeId)!;
      return {
        entityId: e.id,
        refKey: e.refKey,
        keys: [e.refKey, ...(e.aliases ?? [])],
        title: entityName(e, type) || type.name,
        typeId: type.id,
        typeName: type.name,
        typeColor: type.color,
        near: near.has(e.id),
        sort: type.sort,
      };
    })
    .sort((a, b) => Number(b.near) - Number(a.near) || a.sort - b.sort || a.title.localeCompare(b.title, 'de'))
    .map(({ sort: _sort, ...o }) => o);
}

/** Format, in dem ein Verweis aus der Auswahl eingefügt wird: `[@schlüssel]`. */
export const refToken = (refKey: string): string => `[@${refKey}]`;
